import { useCallback, useState } from "react";
import * as Auth from 'aws-amplify/auth';
import { Grid, Typography } from "@mui/material";
import LoginHeader from "../Login/LoginHeader";
import ChangeAccount from "../Login/ChangeAccount";
import NewPassword from "../Password/NewPassword";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import PrimaryLink from "../../components-redesign/StyledLinks/PrimaryLink";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import { validatePasscode } from "../../utilities/validations";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
});

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
}));

const InstructionsContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(3),
  alignSelf: "stretch",
}));

const ResendCodeLinkContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignSelf: "flex-start",
  justifyContent: "left",
  marginBottom: theme.spacing(3),
}));

interface SetPasswordProps {
  username: string;
  isLoading: boolean;
  setIsLoading: (loading) => void
  setResetPasswordState: () => void;
  onBack: () => void;
}

const SetPassword = ({
  username,
  isLoading,
  setIsLoading,
  setResetPasswordState,
  onBack,
}: SetPasswordProps) => {

  const { _translate } = useLocalTranslationState();

  const [password, setPassword] = useState("");
  const [passcode, setPasscode] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passcodeError, setPasscodeError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [, setResetPassword] = useState<any>();

  const resendPasscode = useCallback(async () => {
    setIsLoading(true);
    setPasscode('');
    try {
      await Auth.resetPassword({ username });
      setPasscodeError('');
      setPasswordError('');
    } catch (error) {
      console.log("Caught aws-amplify/auth error:", error);
      setPasscodeError(error.message)
    }
    setIsLoading(false);
  }, []);

  const isValid = () => {
    if (passcode) {
      if (!validatePasscode(passcode)) {
        setPasscodeError(_translate("Error, please correct this field"))
        return false
      }
    } else {
      setPasscodeError(_translate("Error, please complete this field"))
      return false
    }
    if (!password) {
      setPasswordError(_translate("Error, please complete this field"))
      return false
    }
    return isPasswordValid
  };

  const confirmReset = useCallback(async (email: string, password: string, passcode: string) => {
    const confirmOutput = await Auth.confirmResetPassword({
      username: email,
      newPassword: password,
      confirmationCode: passcode
    });
    return confirmOutput;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (isValid()) {
      try {
        const confirmOutput = await confirmReset(username, password, passcode)
        setResetPassword(confirmOutput);
        setResetPasswordState();
      } catch (error) {
        console.log("Caught error:", error);
        setPasswordError(error.message)
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        <TextContainer>
          <Typography variant="subtitle2" fontWeight="400">
            {_translate("Step 2 of 2")}
          </Typography>
        </TextContainer>
        <HeaderContainer>
          <LoginHeader text={_translate("Reset your password")} />
        </HeaderContainer>
        <ChangeAccount
          username={username}
          onClick={() => {
            setPasscodeError("")
            setPasswordError("")
            onBack()
          }} />
        <InstructionsContainer>
          <Typography variant="subtitle1" fontWeight="400">
            {_translate("Please enter the 6-digit verification code")}
          </Typography>
        </InstructionsContainer>

        <TextInput
          fieldName="passcode"
          id="passcode"
          dataTestId="passcode"
          label={_translate("Verification code")}
          type={'text'}
          value={passcode}
          isDisabled={isLoading}
          overrideAutoComplete={true}
          isRequired={true}
          errorInPlainText={false}
          error={passcodeError}
          onClear={() => setPasscode('')}
          onChange={(event) => {
            const value = event.target.value;
            setPasscode(() => value);
            if (value) {
              setPasscodeError('')
            }
          }}
        />

        <Grid container
          sx={(theme) => ({
            paddingTop: theme.spacing(1),
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          })}
          wrap="nowrap"
          justifyContent="space-between">
          <Grid sx={{ width: '100%' }}>
            <ResendCodeLinkContainer>
              <PrimaryLink
                text={_translate("Resend the code")}
                onClick={() => resendPasscode()} />
            </ResendCodeLinkContainer>
          </Grid>
        </Grid>

        <NewPassword
          password={password}
          label={_translate("Password")}
          setPassword={setPassword}
          error={passwordError}
          overrideAutoComplete={true}
          setError={setPasswordError}
          setIsValid={setIsPasswordValid} />

        <Grid container
          sx={(theme) => ({
            paddingTop: theme.spacing(4),
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          })}
          wrap="nowrap"
          justifyContent="space-between">
          <Grid sx={{ width: '100%' }}>
            <LoginBtnContainer>
              <PrimaryButton
                id="login-password-btn"
                text={_translate("ConfirmNewPassword")}
                dataTestId="password-btn"
              />
            </LoginBtnContainer>
          </Grid>
        </Grid>
      </FormContainer>
    </>
  );
};

export default SetPassword;